import React, { useState, useEffect } from "react";
import { X, MapPin, CheckCircle, XCircle, Loader } from "lucide-react";
import CityManager from "../../../services/home/CityManager";
import toast from "react-hot-toast";

export const ServiceabilityCheckModal = ({ isOpen, onClose }) => {
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedPincode, setSelectedPincode] = useState("");
  const [cities, setCities] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // null, "available", "not_available"
  const [step, setStep] = useState("input"); // "input" or "result"

  // Fetch cities on mount
  useEffect(() => {
    if (isOpen) {
      CityManager.getActiveCitiesWithPincodes()
        .then((data) => {
          setCities(data || []);
        })
        .catch(() => {
          toast.error("Failed to load cities");
        });
    }
  }, [isOpen]);

  // Update pincodes when city changes
  useEffect(() => {
    if (selectedCity) {
      const city = cities.find((c) => c.cityId === parseInt(selectedCity));
      setPincodes(city?.pincodes || []);
      setSelectedPincode(""); // Reset pincode
    } else {
      setPincodes([]);
    }
  }, [selectedCity, cities]);

  const handleCheck = async () => {
    if (!selectedCity || !selectedPincode) {
      toast.error("Please select both city and pincode");
      return;
    }

    setLoading(true);
    try {
      // Find the selected city and pincode objects
      const city = cities.find((c) => c.cityId === parseInt(selectedCity));
      const pincode = pincodes.find((p) => p.pinId === parseInt(selectedPincode));

      if (city && pincode && city.active && pincode.active) {
        setResult("available");
      } else {
        setResult("not_available");
      }
      setStep("result");
    } catch (error) {
      toast.error("Error checking serviceability");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedCity("");
    setSelectedPincode("");
    setResult(null);
    setStep("input");
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 mt-20">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
        >
          <X size={24} />
        </button>

        {step === "input" ? (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <MapPin className="text-primary" size={28} />
              <h2 className="text-2xl font-bold text-gray-800">
                Check Serviceability
              </h2>
            </div>

            {/* City Selection */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select City
              </label>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
              >
                <option value="">-- Choose a city --</option>
                {cities.map((city) => (
                  <option key={city.cityId} value={city.cityId}>
                    {city.cityName}, {city.state}
                  </option>
                ))}
              </select>
            </div>

            {/* Pincode Selection */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select Pincode
              </label>
              <select
                value={selectedPincode}
                onChange={(e) => setSelectedPincode(e.target.value)}
                disabled={!selectedCity}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">-- Choose a pincode --</option>
                {pincodes.map((pin) => (
                  <option key={pin.pinId} value={pin.pinId}>
                    {pin.pinCode}
                  </option>
                ))}
              </select>
            </div>

            {/* Check Button */}
            <button
              onClick={handleCheck}
              disabled={!selectedCity || !selectedPincode || loading}
              className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader size={18} className="animate-spin" />
                  Checking...
                </>
              ) : (
                "Check Availability"
              )}
            </button>
          </>
        ) : (
          <>
            {/* Result Display */}
            <div className="text-center">
              {result === "available" ? (
                <>
                  <CheckCircle className="text-green-500 mx-auto mb-4" size={64} />
                  <h3 className="text-2xl font-bold text-green-600 mb-2">
                    Delivery Available
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Great! We deliver to {cities.find((c) => c.cityId === parseInt(selectedCity))?.cityName} at pincode{" "}
                    <span className="font-semibold">
                      {pincodes.find((p) => p.pinId === parseInt(selectedPincode))?.pinCode}
                    </span>
                  </p>
                </>
              ) : (
                <>
                  <XCircle className="text-red-500 mx-auto mb-4" size={64} />
                  <h3 className="text-2xl font-bold text-red-600 mb-2">
                    Currently Not Serviceable
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Sorry, we don't currently deliver to {cities.find((c) => c.cityId === parseInt(selectedCity))?.cityName} at pincode{" "}
                    <span className="font-semibold">
                      {pincodes.find((p) => p.pinId === parseInt(selectedPincode))?.pinCode}
                    </span>
                    . Please check other available areas.
                  </p>
                </>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleReset}
                  className="flex-1 bg-gray-200 text-gray-800 py-2.5 rounded-lg font-semibold hover:bg-gray-300 transition"
                >
                  Check Another
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 bg-primary text-white py-2.5 rounded-lg font-semibold hover:bg-orange-600 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceabilityCheckModal;
